import type { Schemas } from '@argzombie/api-client';
import { useId, useState, type ReactNode } from 'react';
import type { FieldErrors, Issue } from '../../lib/validate';
import { listOps as list } from './listOps';
import { useItemOptions, type Option } from './refs';

type Stack = Schemas['ItemStack'];

export const inputCls =
  'w-full rounded border-2 border-ink bg-white px-2 py-1 text-sm aria-[invalid=true]:border-blam';
export const smallBtn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/50 disabled:opacity-40';

/** Issues shown under a field: errors in red (the server rejects them), warnings in bruise (client-only checks). */
export function IssueList({ issues }: { issues?: Issue[] }) {
  if (!issues?.length) return null;
  return (
    <ul className="mt-0.5 text-xs">
      {issues.map((i, n) => (
        <li
          key={n}
          className={i.severity === 'error' ? 'text-blam' : 'text-bruise italic'}
          role={i.severity === 'error' ? 'alert' : undefined}
        >
          {i.severity === 'warning' ? 'Warning: ' : ''}
          {i.message}
        </li>
      ))}
    </ul>
  );
}

const hasError = (issues?: Issue[]) => !!issues?.some((i) => i.severity === 'error');

/** A labelled control with its hint and inline issues. `children` receives the input's id. */
export function Field({
  label,
  hint,
  issues,
  children,
  className,
}: {
  label: string;
  hint?: string;
  issues?: Issue[];
  children: (id: string, invalid: boolean) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wide">
        {label}
      </label>
      {children(id, hasError(issues))}
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
      <IssueList issues={issues} />
    </div>
  );
}

/** A group of fields with a heading, laid out in a responsive grid. */
export function Fieldset({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3">
      <legend className="px-1 text-sm font-bold">{legend}</legend>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

const fmt = (v: number | null | undefined) => (v == null ? '' : String(v));

/**
 * A number input that keeps what the designer types (so "0." or "-" don't get eaten) and reports a number, or null
 * when empty and `nullable`. `integer` rounds toward what the server stores (int32 fields).
 */
export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  step,
  integer,
  nullable,
  invalid,
  label,
  placeholder,
}: {
  id?: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  nullable?: boolean;
  invalid?: boolean;
  label?: string;
  placeholder?: string;
}) {
  const [text, setText] = useState(fmt(value));
  const [seen, setSeen] = useState(value);
  // Adopt outside changes (JSON edits, another field) without clobbering an in-progress edit of the same number.
  if (value !== seen) {
    setSeen(value);
    if (Number(text) !== value || text.trim() === '') setText(fmt(value));
  }
  return (
    <input
      id={id}
      aria-label={label}
      aria-invalid={invalid}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      className={inputCls}
      value={text}
      min={min}
      max={max}
      step={step ?? (integer ? 1 : 'any')}
      placeholder={placeholder ?? (nullable ? 'none' : undefined)}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === '') {
          const next = nullable ? null : 0;
          setSeen(next);
          onChange(next);
          return;
        }
        const n = Number(t);
        if (!Number.isFinite(n)) return;
        const next = integer ? Math.trunc(n) : n;
        setSeen(next);
        onChange(next);
      }}
    />
  );
}

const pretty = (v: unknown) => JSON.stringify(v ?? [], null, 2);

/**
 * A list of objects edited as JSON text (e.g. a boss's plates, phases, attacks). The draft only changes when the text
 * parses to a list of objects; until then the parse problem shows inline. `issues` are the validator's issues for the
 * list and its entries. `make` adds a default entry (only while the text is valid).
 */
export function JsonListField<T extends object>({
  label,
  hint,
  value,
  onChange,
  issues,
  make,
  what,
}: {
  label: string;
  hint?: string;
  value: T[] | null | undefined;
  onChange: (next: T[]) => void;
  issues?: Issue[];
  make?: () => T;
  what?: string;
}) {
  const id = useId();
  const current = pretty(value);
  const [text, setText] = useState(current);
  const [seen, setSeen] = useState(current);
  const [problem, setProblem] = useState<string | null>(null);
  // Adopt outside changes (JSON tab, Add button) unless they're what this text already says.
  if (current !== seen) {
    setSeen(current);
    setText(current);
    setProblem(null);
  }
  const edit = (t: string) => {
    setText(t);
    let parsed: unknown;
    try {
      parsed = JSON.parse(t);
    } catch (e) {
      setProblem(`Not valid JSON: ${(e as Error).message}`);
      return;
    }
    if (
      !Array.isArray(parsed) ||
      parsed.some((x) => x === null || typeof x !== 'object' || Array.isArray(x))
    ) {
      setProblem('Must be a JSON list of objects: [ { ... }, ... ].');
      return;
    }
    setProblem(null);
    setSeen(pretty(parsed));
    onChange(parsed as T[]);
  };
  return (
    <div className="sm:col-span-2">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wide">
          {label}
        </label>
        {make && (
          <button
            type="button"
            className={smallBtn}
            disabled={problem !== null}
            onClick={() => onChange([...(value ?? []), make()])}
          >
            Add {what ?? 'entry'}
          </button>
        )}
      </div>
      <textarea
        id={id}
        aria-invalid={problem !== null || hasError(issues)}
        spellCheck={false}
        className={`${inputCls} h-48 font-mono text-xs`}
        value={text}
        onChange={(e) => edit(e.target.value)}
      />
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
      {problem && (
        <p role="alert" className="mt-0.5 text-xs text-blam">
          {problem} Not saved until it parses.
        </p>
      )}
      <IssueList issues={issues} />
    </div>
  );
}

/** Shorthand: a labelled number field bound to one key. */
export function NumberField(
  props: {
    label: string;
    hint?: string;
    issues?: Issue[];
  } & Omit<Parameters<typeof NumberInput>[0], 'id' | 'invalid' | 'label'>,
) {
  const { label, hint, issues, ...rest } = props;
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(id, invalid) => <NumberInput id={id} invalid={invalid} {...rest} />}
    </Field>
  );
}

export function TextField({
  label,
  hint,
  issues,
  value,
  onChange,
  multiline,
  mono,
}: {
  label: string;
  hint?: string;
  issues?: Issue[];
  value: string | null | undefined;
  onChange: (v: string) => void;
  multiline?: boolean;
  mono?: boolean;
}) {
  return (
    <Field
      label={label}
      hint={hint}
      issues={issues}
      className={multiline ? 'sm:col-span-2' : undefined}
    >
      {(id, invalid) =>
        multiline ? (
          <textarea
            id={id}
            aria-invalid={invalid}
            className={`${inputCls} h-16`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            id={id}
            aria-invalid={invalid}
            className={`${inputCls} ${mono ? 'font-mono' : ''}`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    </Field>
  );
}

/**
 * A select. `none` adds an empty choice that reports null. A current value that isn't among the options stays
 * selectable (marked unknown) so the form never silently changes data it doesn't recognise.
 */
export function SelectInput({
  id,
  value,
  options,
  onChange,
  none,
  invalid,
  label,
}: {
  id?: string;
  value: string | null | undefined;
  options: Option[];
  onChange: (v: string | null) => void;
  none?: string;
  invalid?: boolean;
  label?: string;
}) {
  const known = value == null || value === '' || options.some((o) => o.value === value);
  return (
    <select
      id={id}
      aria-label={label}
      aria-invalid={invalid}
      className={inputCls}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    >
      {(none !== undefined || value == null || value === '') && (
        <option value="">{none ?? '(choose)'}</option>
      )}
      {!known && <option value={value}>{value} (not listed)</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function SelectField(
  props: { label: string; hint?: string; issues?: Issue[] } & Omit<
    Parameters<typeof SelectInput>[0],
    'id' | 'invalid' | 'label'
  >,
) {
  const { label, hint, issues, ...rest } = props;
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(id, invalid) => <SelectInput id={id} invalid={invalid} {...rest} />}
    </Field>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
    </div>
  );
}

export function ItemPicker(
  props: Omit<Parameters<typeof SelectInput>[0], 'options'> & {
    filter?: (category: string) => boolean;
  },
) {
  const { filter, ...rest } = props;
  const options = useItemOptions(filter);
  return <SelectInput {...rest} options={options} />;
}

/** Move, remove controls for one row of a list editor. */
export function RowControls({
  index,
  count,
  onMove,
  onRemove,
  what,
}: {
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  what: string;
}) {
  return (
    <div className="flex shrink-0 gap-1">
      <button
        type="button"
        className={smallBtn}
        aria-label={`Move ${what} ${index + 1} up`}
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      >
        ↑
      </button>
      <button
        type="button"
        className={smallBtn}
        aria-label={`Move ${what} ${index + 1} down`}
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
      >
        ↓
      </button>
      <button
        type="button"
        className={`${smallBtn} text-blam`}
        aria-label={`Remove ${what} ${index + 1}`}
        onClick={() => onRemove(index)}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * A generic list editor: one row per element, rendered by `row`, with move/remove controls and an Add button.
 * Issues keyed at the list's own path are shown under it.
 */
export function ListEditor<T>({
  label,
  items,
  onChange,
  make,
  row,
  what,
  issues,
  hint,
}: {
  label: string;
  items: T[];
  onChange: (next: T[]) => void;
  make: () => T;
  row: (item: T, index: number, set: (v: T) => void) => ReactNode;
  what: string;
  issues?: Issue[];
  hint?: string;
}) {
  return (
    <div className="sm:col-span-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
        <button type="button" className={smallBtn} onClick={() => onChange([...items, make()])}>
          Add {what}
        </button>
      </div>
      {hint && <p className="text-xs text-bruise">{hint}</p>}
      {items.length === 0 && <p className="text-xs text-bruise">None.</p>}
      <ol className="mt-1 space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 rounded border border-newsprint p-1">
            <span className="w-5 shrink-0 pt-1 text-right font-mono text-xs text-bruise">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              {row(item, i, (v) => onChange(list.set(items, i, v)))}
            </div>
            <RowControls
              index={i}
              count={items.length}
              what={what}
              onMove={(a, b) => onChange(list.move(items, a, b))}
              onRemove={(a) => onChange(list.remove(items, a))}
            />
          </li>
        ))}
      </ol>
      <IssueList issues={issues} />
    </div>
  );
}

/** Item + quantity rows (recipe inputs/outputs, module costs, starter kits). */
export function StackListEditor({
  label,
  value,
  onChange,
  errors,
  base,
  hint,
}: {
  label: string;
  value: Stack[] | undefined;
  onChange: (next: Stack[]) => void;
  errors: FieldErrors;
  base: string;
  hint?: string;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      items={value ?? []}
      onChange={onChange}
      what="item"
      issues={errors[base]}
      make={() => ({ itemId: '', quantity: 1 })}
      row={(s, j, set) => (
        <div className="grid grid-cols-[minmax(0,1fr)_6rem] gap-2">
          <div>
            <ItemPicker
              label={`${label} ${j + 1} item`}
              value={s.itemId}
              invalid={hasError(errors[`${base}.${j}.itemId`])}
              onChange={(v) => set({ ...s, itemId: v ?? '' })}
            />
            <IssueList issues={errors[`${base}.${j}.itemId`]} />
          </div>
          <div>
            <NumberInput
              label={`${label} ${j + 1} quantity`}
              integer
              min={1}
              value={s.quantity}
              invalid={hasError(errors[`${base}.${j}.quantity`])}
              onChange={(v) => set({ ...s, quantity: v ?? 0 })}
            />
            <IssueList issues={errors[`${base}.${j}.quantity`]} />
          </div>
        </div>
      )}
    />
  );
}

/** A list of item ids (food/water items, likely loot). */
export function ItemIdListEditor({
  label,
  value,
  onChange,
  errors,
  base,
  hint,
}: {
  label: string;
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  errors: FieldErrors;
  base: string;
  hint?: string;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      items={value ?? []}
      onChange={onChange}
      what="item"
      issues={errors[base]}
      make={() => ''}
      row={(id, j, set) => (
        <>
          <ItemPicker
            label={`${label} ${j + 1}`}
            value={id}
            invalid={hasError(errors[`${base}.${j}`])}
            onChange={(v) => set(v ?? '')}
          />
          <IssueList issues={errors[`${base}.${j}`]} />
        </>
      )}
    />
  );
}

/** A list of free-text strings (OSM tags, custom item tags). */
export function StringListEditor({
  label,
  value,
  onChange,
  errors,
  base,
  hint,
  placeholder,
}: {
  label: string;
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  errors: FieldErrors;
  base: string;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      items={value ?? []}
      onChange={onChange}
      what="entry"
      issues={errors[base]}
      make={() => ''}
      row={(s, j, set) => (
        <>
          <input
            aria-label={`${label} ${j + 1}`}
            className={`${inputCls} font-mono`}
            value={s}
            placeholder={placeholder}
            onChange={(e) => set(e.target.value)}
          />
          <IssueList issues={errors[`${base}.${j}`]} />
        </>
      )}
    />
  );
}
